/**
 * V2TemplateStructMerger - Merges simplified V2 content onto V2 theme template StepNode
 *
 * Theme templates provide the visual structure (appearance, layout, canvas).
 * Simplified content from AI provides text (plain strings), image URLs, and actions.
 *
 * This produces full V2 StepNodes that screen-view-v2 can render with proper styling.
 *
 * Merge strategy:
 * - Deep-clone template StepNode content (preserves all styles)
 * - Overlay simplified content: replace translations text, image URLs, actions
 * - Preserve template: appearance, layout, kind, canvas, all styling
 * - Leakage prevention: if simplified omits optional field → delete from result
 *
 * @module screen-utils/services
 */

import type {
  SimplifiedV2Content,
  SimplifiedCopyBlock,
  SimplifiedTapTarget,
  SimplifiedHeaderBar,
  SimplifiedActionBar,
  SimplifiedOptionsList,
  SimplifiedCarouselConfig,
  SimplifiedStepperConfig,
  SimplifiedTextFieldConfig,
  SimplifiedRollerConfig,
  SimplifiedEmbedConfig,
  SimplifiedPaywallConfig,
  AiAsset,
} from '@segmently/schema-models/simplified';

import type {
  StepNode,
  StepContent,
  LocalizedLabel,
  LocalizedImage,
  TapTarget,
  AssetReference,
} from '@segmently/schema-models/v2';

type LegacyAssetContent = SimplifiedV2Content & {
  heroAsset?: AiAsset;
  featuredMediaAsset?: AiAsset;
};

type CanonicalSimplifiedTapTarget = SimplifiedTapTarget & { label?: string };

function readTapTargetText(value: SimplifiedTapTarget): string | undefined {
  const target = value as CanonicalSimplifiedTapTarget;
  return typeof target.label === 'string' ? target.label : target.content;
}

function readLegacyImageAsset(value: unknown): AiAsset | undefined {
  if (!value || typeof value !== 'object') return undefined;
  return (value as { imageAsset?: AiAsset }).imageAsset;
}

// ═══════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════

function deepClone<T>(obj: T): T {
  if (obj === null || obj === undefined) return obj;
  return JSON.parse(JSON.stringify(obj));
}

/**
 * Overlay plain string text onto a template LocalizedLabel.
 * Preserves: kind, appearance, layout, templateParams from template.
 * Replaces: translations[lang] with the new text.
 */
function overlayLabel(
  templateLabel: LocalizedLabel | undefined,
  text: string | undefined,
  lang: string,
): LocalizedLabel | undefined {
  if (text == null) return templateLabel;
  if (!templateLabel) {
    // No template — create minimal label
    return {
      translations: { [lang]: text },
      kind: 'static' as any,
      appearance: {} as any,
    };
  }
  // Clone template label, replace only translations
  const result = deepClone(templateLabel);
  result.translations = { ...result.translations, [lang]: text };
  return result;
}

/**
 * Overlay URL onto a template LocalizedImage.
 * Preserves: appearance, layout from template.
 * Replaces: translations[lang] asset reference.
 */
function overlayImage(
  templateImage: LocalizedImage | undefined,
  url: string | undefined,
  lang: string,
): LocalizedImage | undefined {
  if (!url) return templateImage;
  if (!templateImage) {
    return {
      translations: { [lang]: { original: url } as AssetReference },
      appearance: {} as any,
    };
  }
  const result = deepClone(templateImage);
  if (!result.translations) result.translations = {};
  result.translations[lang] = { original: url } as AssetReference;
  return result;
}

/**
 * Overlay simplified tap target onto template TapTarget.
 * Preserves: kind, appearance, layout, icon, iconPlacement, iconInsets from template.
 * Replaces: content text (in translations), action (trigger with edges).
 */
function overlayTapTarget(
  template: TapTarget | undefined,
  simplified: SimplifiedTapTarget | undefined,
  lang: string,
): TapTarget | undefined {
  if (!simplified) return undefined; // Leakage prevention: don't keep template button if AI didn't include it
  const text = readTapTargetText(simplified);
  if (!template) {
    // No template — create minimal tap target
    return {
      kind: 'label' as any,
      content: {
        translations: { [lang]: text ?? '' },
        kind: 'static' as any,
        appearance: {} as any,
      } as any,
      appearance: {} as any,
      action: simplified.action,
    };
  }
  const result = deepClone(template);
  // Replace content text
  if (result.content && 'translations' in result.content) {
    (result.content as LocalizedLabel).translations = {
      ...(result.content as LocalizedLabel).translations,
      [lang]: text ?? '',
    };
  } else {
    result.content = {
      translations: { [lang]: text ?? '' },
      kind: 'static' as any,
      appearance: {} as any,
    } as any;
  }
  // Replace action
  result.action = simplified.action;
  return result;
}

// ═══════════════════════════════════════════════════════
// SECTION MERGERS
// ═══════════════════════════════════════════════════════

function mergeCopy(
  templateContent: StepContent,
  copy: SimplifiedCopyBlock | undefined,
  lang: string,
): void {
  if (!copy) return;
  if (!templateContent.copy) templateContent.copy = {} as any;
  if (copy.title != null) {
    templateContent.copy!.title = overlayLabel(templateContent.copy!.title, copy.title, lang)!;
  }
  if (copy.subtitle != null) {
    templateContent.copy!.subtitle = overlayLabel(templateContent.copy!.subtitle, copy.subtitle, lang);
  }
  // subtitle2 removed from CopyBlock in v2.18.0
}

function mergeHero(
  templateContent: StepContent,
  heroAsset: AiAsset | undefined,
  lang: string,
): void {
  if (!heroAsset?.generatedUrl) return;
  templateContent.hero = overlayImage(templateContent.hero, heroAsset.generatedUrl, lang);
}

function mergeFeaturedMedia(
  templateContent: StepContent,
  asset: AiAsset | undefined,
  lang: string,
): void {
  if (!asset?.generatedUrl) return;
  if (!templateContent.featuredMedia) {
    // MediaKind discriminant must be 'Image' — the renderer matches
    // kind === 'Image' | 'Video' and returns null for anything else, so a
    // synthesized 'photo' slot never rendered (silent missing hero on screens
    // whose template lacks a featuredMedia, e.g. List/Grid reproduction).
    templateContent.featuredMedia = { kind: 'Image', content: {}, appearance: {} } as any;
  }
  const fm = templateContent.featuredMedia as any;
  if (!fm.content) fm.content = {};
  fm.content.image = overlayImage(fm.content?.image, asset.generatedUrl, lang);
}

function mergeHeader(
  templateContent: StepContent,
  header: SimplifiedHeaderBar | undefined,
  lang: string,
): void {
  // Leakage prevention: if simplified omits header → delete template's header
  if (!header) {
    delete templateContent.header;
    return;
  }
  if (!templateContent.header) templateContent.header = {} as any;
  const h = templateContent.header!;

  if (header.skip) {
    h.skip = overlayTapTarget(h.skip, header.skip, lang);
  } else {
    delete h.skip; // AI didn't include skip → remove template's
  }

  if (header.back) {
    h.back = overlayTapTarget(h.back, header.back, lang);
  } else {
    delete h.back;
  }

  if (header.close) {
    h.close = overlayTapTarget(h.close, header.close, lang);
  } else {
    delete h.close;
  }
}

function mergeActionBar(
  templateContent: StepContent,
  actionBar: SimplifiedActionBar | undefined,
  lang: string,
): void {
  if (!actionBar) {
    delete templateContent.actionBar;
    return;
  }
  if (!templateContent.actionBar) templateContent.actionBar = {} as any;
  const ab = templateContent.actionBar!;

  if (actionBar.primary) {
    ab.primary = overlayTapTarget(ab.primary, actionBar.primary, lang);
  } else {
    delete ab.primary;
  }

  if (actionBar.secondary) {
    ab.secondary = overlayTapTarget(ab.secondary, actionBar.secondary, lang);
  } else {
    delete ab.secondary;
  }
}

function mergeOptions(
  templateContent: StepContent,
  options: SimplifiedOptionsList | undefined,
  lang: string,
): void {
  if (!options?.items?.length) return;
  if (!templateContent.options) templateContent.options = { items: [] } as any;
  const tOpts = templateContent.options!;

  // Use template's first item as visual skeleton for all items
  const skeletonItem = tOpts.items?.[0] ? deepClone(tOpts.items[0]) : {} as any;

  tOpts.items = options.items.map((simplified) => {
    const item = deepClone(skeletonItem);
    const imageAsset = readLegacyImageAsset(simplified);
    // Overlay content
    item.title = overlayLabel(item.title, simplified.title, lang)!;
    if (simplified.subtitle != null) {
      item.subtitle = overlayLabel(item.subtitle, simplified.subtitle, lang);
    }
    if (imageAsset?.generatedUrl) {
      item.image = overlayImage(item.image, imageAsset.generatedUrl, lang);
    }
    if (simplified.action) {
      item.action = simplified.action;
    }
    item.variableValue = simplified.variableValue;
    if (simplified.weight != null) item.weight = simplified.weight;
    return item;
  });

  // Overlay list-level settings
  if (options.selectionMode != null) tOpts.selectionMode = options.selectionMode as any;
  if (options.isRandomized != null) tOpts.isRandomized = options.isRandomized;
  if (options.maxSelectedItemCount != null) tOpts.maxSelectedItemCount = options.maxSelectedItemCount;
  // W8 (R-W70-37/38): the disclosure copy generation emits overlays the
  // template's (or a previous compile's) label the same way item titles do; a
  // generation that emits neither leaves whatever the template carries.
  if (options.moreOptionsLabel != null) tOpts.moreOptionsLabel = overlayLabel(tOpts.moreOptionsLabel ?? undefined, options.moreOptionsLabel, lang);
  if (options.fewerOptionsLabel != null) tOpts.fewerOptionsLabel = overlayLabel(tOpts.fewerOptionsLabel ?? undefined, options.fewerOptionsLabel, lang);
}

function mergeCarousel(
  templateContent: StepContent,
  carousel: SimplifiedCarouselConfig | undefined,
  lang: string,
): void {
  if (!carousel?.items?.length) return;
  if (!templateContent.carousel) templateContent.carousel = {} as any;
  const tCarousel = templateContent.carousel! as any;

  const skeletonSlide = tCarousel.items?.[0] ? deepClone(tCarousel.items[0]) : { content: {} };

  tCarousel.items = carousel.items.map((simplified, index) => {
    const slide = deepClone(tCarousel.items?.[index] ?? skeletonSlide);
    const imageAsset = readLegacyImageAsset(simplified);
    const fallbackProgress = progressRange(index, carousel.items.length);
    slide.progressStart = typeof slide.progressStart === 'number' ? slide.progressStart : fallbackProgress.progressStart;
    slide.progressEnd = typeof slide.progressEnd === 'number' ? slide.progressEnd : fallbackProgress.progressEnd;
    if (!slide.content) slide.content = {};
    if (simplified.title != null) {
      slide.content.title = overlayLabel(slide.content.title, simplified.title, lang);
    }
    if (simplified.subtitle != null) {
      slide.content.subtitle = overlayLabel(slide.content.subtitle, simplified.subtitle, lang);
    }
    if (simplified.subtitle2 != null) {
      (slide.content as any).detail = overlayLabel((slide.content as any).detail, simplified.subtitle2, lang);
    }
    if (imageAsset?.generatedUrl) {
      slide.content.image = overlayImage(slide.content.image, imageAsset.generatedUrl, lang);
    }
    return slide;
  });

  // Overlay timer action
  if (carousel.timer?.action) {
    if (!tCarousel.timer) tCarousel.timer = {};
    tCarousel.timer.duration = carousel.timer.duration;
    tCarousel.timer.action = carousel.timer.action;
  }
}

function mergeStepper(
  templateContent: StepContent,
  stepper: SimplifiedStepperConfig | undefined,
  lang: string,
): void {
  if (!stepper?.items?.length) return;
  if (!templateContent.stepper) templateContent.stepper = {} as any;
  const tStepper = templateContent.stepper! as any;

  const skeletonSegment = tStepper.items?.[0] ? deepClone(tStepper.items[0]) : { content: {} };

  tStepper.items = stepper.items.map((simplified, index) => {
    const seg = deepClone(tStepper.items?.[index] ?? skeletonSegment);
    const imageAsset = readLegacyImageAsset(simplified);
    const fallbackProgress = progressRange(index, stepper.items.length);
    seg.progressStart = typeof seg.progressStart === 'number' ? seg.progressStart : fallbackProgress.progressStart;
    seg.progressEnd = typeof seg.progressEnd === 'number' ? seg.progressEnd : fallbackProgress.progressEnd;
    if (!seg.content) seg.content = {};
    if (simplified.title != null) seg.content.title = overlayLabel(seg.content.title, simplified.title, lang);
    if (simplified.subtitle != null) seg.content.subtitle = overlayLabel(seg.content.subtitle, simplified.subtitle, lang);
    if (simplified.description != null) seg.content.description = overlayLabel(seg.content.description, simplified.description, lang);
    if (imageAsset?.generatedUrl) {
      seg.content.image = overlayImage(seg.content.image, imageAsset.generatedUrl, lang);
    }
    return seg;
  });

  if (stepper.timer?.action) {
    if (!tStepper.timer) tStepper.timer = {};
    tStepper.timer.duration = stepper.timer.duration;
    tStepper.timer.action = stepper.timer.action;
  }
}

function mergeTextField(
  templateContent: StepContent,
  field: SimplifiedTextFieldConfig | undefined,
  lang: string,
): void {
  if (!field) return;
  if (!templateContent.textField) templateContent.textField = {} as any;
  const tf = templateContent.textField! as any;
  if (field.placeholder != null) tf.placeholder = overlayLabel(tf.placeholder, field.placeholder, lang);
  if (field.errorMessage != null) tf.errorMessage = overlayLabel(tf.errorMessage, field.errorMessage, lang);
}

function mergeRoller(
  templateContent: StepContent,
  roller: SimplifiedRollerConfig | undefined,
  lang: string,
): void {
  if (!roller?.wheels?.length) return;
  if (!templateContent.roller) templateContent.roller = {} as any;
  const tRoller = templateContent.roller! as any;

  tRoller.wheels = roller.wheels.map((wheel, wi) => {
    const templateWheel = tRoller.wheels?.[wi] ? deepClone(tRoller.wheels[wi]) : {};
    templateWheel.options = wheel.options.map((opt, oi) => {
      const templateOpt = templateWheel.options?.[oi] ? deepClone(templateWheel.options[oi]) : {};
      templateOpt.label = overlayLabel(templateOpt.label, opt.label, lang);
      return templateOpt;
    });
    return templateWheel;
  });
}

function mergeEmbed(
  templateContent: StepContent,
  embed: SimplifiedEmbedConfig | undefined,
): void {
  if (!embed) return;
  if (!templateContent.embed) templateContent.embed = {} as any;
  const tEmbed = templateContent.embed! as any;
  if (embed.html != null) tEmbed.html = embed.html;
  if (embed.callback?.action) {
    if (!tEmbed.callback) tEmbed.callback = {};
    tEmbed.callback.action = embed.callback.action;
  }
}

function mergePaywall(
  templateContent: StepContent,
  paywall: SimplifiedPaywallConfig | undefined,
  lang: string,
): void {
  if (!paywall) return;
  if (!templateContent.paywall) templateContent.paywall = {} as any;
  const tPaywall = templateContent.paywall! as any;

  // Products
  if (paywall.products?.length) {
    const skeletonProduct = tPaywall.products?.items?.[0] ? deepClone(tPaywall.products.items[0]) : {};
    if (!tPaywall.products) tPaywall.products = { items: [] };
    tPaywall.products.items = paywall.products.map((p, index) => {
      const product = deepClone(skeletonProduct);
      if (p.productId != null) product.productId = p.productId;
      if (p.title != null) product.topStartLabel = overlayLabel(product.topStartLabel, p.title, lang);
      if (p.subtitle != null) product.bottomStartLabel = overlayLabel(product.bottomStartLabel, p.subtitle, lang);
      if (p.badge != null) {
        if (!product.badge) product.badge = {};
        product.badge.label = overlayLabel(product.badge?.label, p.badge, lang);
      }
      product.isSelected = p.isDefault ?? product.isSelected ?? false;
      product.weight = product.weight ?? index;
      return product;
    });
  }

  // Purchase action
  if (paywall.purchaseAction) {
    tPaywall.purchaseAction = overlayTapTarget(tPaywall.purchaseAction, paywall.purchaseAction, lang);
  }

  // Close button
  if (paywall.closeButton) {
    tPaywall.closeButton = overlayTapTarget(tPaywall.closeButton, paywall.closeButton, lang);
  }

  // Features
  if (paywall.features?.length) {
    const skeletonFeature = tPaywall.features?.[0] ? deepClone(tPaywall.features[0]) : {};
    tPaywall.features = paywall.features.map((f) => {
      const feat = deepClone(skeletonFeature);
      const imageAsset = readLegacyImageAsset(f);
      if (f.title != null) feat.title = overlayLabel(feat.title, f.title, lang);
      if (imageAsset?.generatedUrl) {
        feat.image = overlayImage(feat.image, imageAsset.generatedUrl, lang);
      }
      return feat;
    });
  }

  // Restore label
  if (paywall.restoreLabel != null) {
    tPaywall.restoreLabel = overlayLabel(tPaywall.restoreLabel, paywall.restoreLabel, lang);
  }
}

function mergeFlexibleLayout(
  templateContent: StepContent,
  flexibleLayout: any,
  lang: string,
): void {
  if (!flexibleLayout) return;
  const templateFlexibleLayout = (templateContent.flexibleLayout ?? {}) as any;
  const templateSections = Array.isArray(templateFlexibleLayout.sections)
    ? templateFlexibleLayout.sections
    : [];
  const simplifiedSections = Array.isArray(flexibleLayout.sections)
    ? flexibleLayout.sections
    : [];

  templateContent.flexibleLayout = {
    ...templateFlexibleLayout,
    scrollable: typeof flexibleLayout.scrollable === 'boolean'
      ? flexibleLayout.scrollable
      : templateFlexibleLayout.scrollable ?? true,
    insets: flexibleLayout.insets ?? templateFlexibleLayout.insets,
    sections: simplifiedSections.map((section: any, index: number) => mergeFlexibleSection(
      findTemplateSection(templateSections, section, index),
      section,
      index,
      lang,
    )),
    stickyContainer: flexibleLayout.stickyContainer ?? templateFlexibleLayout.stickyContainer,
  } as any;
}

function mergeFlexibleSection(
  templateSection: any,
  simplifiedSection: any,
  index: number,
  lang: string,
): any {
  const kind = flexibleSectionKindToFull(simplifiedSection.kind);
  const result = {
    ...(templateSection ? deepClone(templateSection) : {}),
    id: typeof simplifiedSection.id === 'string' && simplifiedSection.id ? simplifiedSection.id : templateSection?.id ?? `section_${index + 1}`,
    order: typeof simplifiedSection.order === 'number' ? simplifiedSection.order : templateSection?.order ?? index,
    kind,
    label: typeof simplifiedSection.label === 'string' ? simplifiedSection.label : templateSection?.label,
    layout: simplifiedSection.layout ?? templateSection?.layout ?? {},
    parentSectionId: typeof simplifiedSection.parentSectionId === 'string'
      ? simplifiedSection.parentSectionId
      : templateSection?.parentSectionId,
  } as any;

  if (simplifiedSection.copyContent) {
    result.textContent = mergeSectionCopy(result.textContent, simplifiedSection.copyContent, lang);
  }
  if (simplifiedSection.optionsContent) {
    result.optionsListContent = mergeSectionOptions(result.optionsListContent, {
      ...simplifiedSection.optionsContent,
      selectionMode: simplifiedSection.optionsContent.selectionMode ?? defaultSelectionModeForSectionKind(kind),
    }, lang);
  }
  if (simplifiedSection.buttonContent) {
    result.buttonContent = overlayTapTarget(result.buttonContent, simplifiedSection.buttonContent, lang);
  }
  if (simplifiedSection.imageContent?.generatedUrl) {
    result.mediaContent = result.mediaContent ?? { kind: 'photo', content: {}, appearance: {} };
    const media = result.mediaContent as any;
    media.content = media.content ?? {};
    media.content.image = overlayImage(media.content.image, simplifiedSection.imageContent.generatedUrl, lang);
  }
  if (simplifiedSection.embedContent) {
    result.embedContent = mergeSectionEmbed(result.embedContent, simplifiedSection.embedContent);
  }
  if (simplifiedSection.paywallHeaderContent) {
    result.paywallHeaderContent = mergeSectionPaywallHeader(
      result.paywallHeaderContent,
      simplifiedSection.paywallHeaderContent,
      lang,
    );
  }

  Object.keys(result).forEach((key) => {
    if (result[key] === undefined) {
      delete result[key];
    }
  });
  return result;
}

function findTemplateSection(templateSections: any[], simplifiedSection: any, index: number): any {
  if (typeof simplifiedSection.id === 'string') {
    const byId = templateSections.find((section) => section.id === simplifiedSection.id);
    if (byId) return byId;
  }
  const byKind = templateSections.find((section) => section.kind === flexibleSectionKindToFull(simplifiedSection.kind));
  return byKind ?? templateSections[index];
}

function mergeSectionCopy(templateCopy: any, copy: SimplifiedCopyBlock, lang: string): any {
  const result = templateCopy ? deepClone(templateCopy) : {};
  if (copy.title != null) result.title = overlayLabel(result.title, copy.title, lang);
  if (copy.subtitle != null) result.subtitle = overlayLabel(result.subtitle, copy.subtitle, lang);
  return result;
}

function mergeSectionOptions(templateOptions: any, options: SimplifiedOptionsList, lang: string): any {
  const result = templateOptions ? deepClone(templateOptions) : { items: [] };
  const skeleton = result.items?.[0] ? deepClone(result.items[0]) : {};
  result.items = options.items.map((item) => {
    const merged = deepClone(skeleton);
    const imageAsset = readLegacyImageAsset(item);
    merged.title = overlayLabel(merged.title, item.title, lang);
    if (item.subtitle != null) merged.subtitle = overlayLabel(merged.subtitle, item.subtitle, lang);
    if (imageAsset?.generatedUrl) merged.image = overlayImage(merged.image, imageAsset.generatedUrl, lang);
    if (item.action) merged.action = item.action;
    merged.variableValue = item.variableValue;
    if (item.weight != null) merged.weight = item.weight;
    return merged;
  });
  if (options.selectionMode != null) result.selectionMode = options.selectionMode;
  if (options.isRandomized != null) result.isRandomized = options.isRandomized;
  if (options.maxSelectedItemCount != null) result.maxSelectedItemCount = options.maxSelectedItemCount;
  return result;
}

function mergeSectionEmbed(templateEmbed: any, embed: SimplifiedEmbedConfig): any {
  const result = templateEmbed ? deepClone(templateEmbed) : {};
  if (embed.html != null) result.html = embed.html;
  if (embed.callback?.action) {
    result.callback = result.callback ?? {};
    result.callback.action = embed.callback.action;
  }
  return result;
}

function mergeSectionPaywallHeader(templatePaywallHeader: any, paywallHeader: any, lang: string): any {
  const result = templatePaywallHeader ? deepClone(templatePaywallHeader) : {};
  if (paywallHeader.closeButton) {
    result.closeButton = overlayTapTarget(result.closeButton, paywallHeader.closeButton, lang);
  }
  if (paywallHeader.restoreLabel != null) {
    result.restoreLabel = overlayLabel(result.restoreLabel, paywallHeader.restoreLabel, lang);
  }
  return result;
}

function flexibleSectionKindToFull(kind: string | undefined): string {
  const mapping: Record<string, string> = {
    copyBlock: 'Text',
    optionsList: 'OptionsList',
    singleSelectionList: 'SingleSelectionList',
    multipleSelectionList: 'MultipleSelectionList',
    bulletList: 'BulletList',
    button: 'Button',
    image: 'Media',
    customEmbed: 'CustomEmbed',
    emptySpace: 'EmptySpace',
    paywallHeader: 'PaywallHeader',
  };
  return mapping[kind ?? ''] ?? kind ?? 'Text';
}

function defaultSelectionModeForSectionKind(kind: string): 'one' | 'many' | 'browse' | undefined {
  if (kind === 'SingleSelectionList') return 'one';
  if (kind === 'MultipleSelectionList') return 'many';
  if (kind === 'BulletList') return 'browse';
  return undefined;
}

function progressRange(index: number, total: number): { progressStart: number; progressEnd: number } {
  const count = Math.max(total, 1);
  return {
    progressStart: Math.round((index / count) * 100),
    progressEnd: Math.round(((index + 1) / count) * 100),
  };
}

// ═══════════════════════════════════════════════════════
// MAIN CLASS
// ═══════════════════════════════════════════════════════

/**
 * Merges simplified V2 content onto a V2 theme template StepNode.
 *
 * Strategy:
 * 1. Deep-clone template StepNode (preserves all canvas, appearance, layout)
 * 2. Overlay simplified content section by section
 * 3. Leakage prevention: if AI omits optional section → delete from result
 *
 * The result is a full StepNode ready for screen-view-v2 rendering.
 */
export class V2TemplateStructMerger {
  /**
   * Merge simplified AI content onto a V2 template StepNode.
   *
   * @param template - Full V2 StepNode from theme (with styles, canvas, layout)
   * @param simplified - Simplified content from AI (plain strings, actions)
   * @param lang - Default language for translations
   * @returns Full StepNode with template styles + simplified content
   */
  merge(
    template: StepNode,
    simplified: SimplifiedV2Content,
    lang: string,
  ): StepNode {
    // Deep-clone template to preserve all styling
    const result = deepClone(template);
    const legacyAssets = simplified as LegacyAssetContent;

    // Overlay each content section
    mergeCopy(result.content, simplified.copy, lang);
    mergeHero(result.content, legacyAssets.heroAsset, lang);
    mergeFeaturedMedia(result.content, legacyAssets.featuredMediaAsset, lang);
    mergeHeader(result.content, simplified.header, lang);
    mergeActionBar(result.content, simplified.actionBar, lang);
    mergeOptions(result.content, simplified.options, lang);
    mergeCarousel(result.content, simplified.carousel, lang);
    mergeStepper(result.content, simplified.stepper, lang);
    mergeTextField(result.content, simplified.textField, lang);
    mergeRoller(result.content, simplified.roller, lang);
    mergeEmbed(result.content, simplified.embed);
    mergePaywall(result.content, simplified.paywall, lang);
    mergeFlexibleLayout(result.content, simplified.flexibleLayout, lang);

    return result;
  }

  /**
   * Re-merge: apply new simplified content while preserving user-edited styles.
   * Used during AI regeneration: styles from current screen, content from new AI output.
   *
   * @param currentScreen - Current full StepNode (with user-edited styles)
   * @param newSimplified - New simplified content from AI
   * @param lang - Default language
   * @returns Updated StepNode: current styles + new content
   */
  remerge(
    currentScreen: StepNode,
    newSimplified: SimplifiedV2Content,
    lang: string,
  ): StepNode {
    // Use current screen AS the template — this preserves user's style edits
    return this.merge(currentScreen, newSimplified, lang);
  }
}

/** Singleton instance */
export const v2TemplateStructMerger = new V2TemplateStructMerger();
